import { Head, Link } from '@inertiajs/react';

/**
 * Issue data from the backend.
 */
interface NewspaperIssue {
    id: number;
    issue_number: number;
    title: string | null;
    display_title: string;
    is_published: boolean;
    created_at: string | null;
}

/**
 * Article data from the backend.
 */
interface NewspaperArticle {
    id: number;
    issue_number: number;
    title: string;
    slug: string;
    byline: string;
    excerpt: string | null;
    cover_image_url: string | null;
    is_published: boolean;
    published_at: string | null;
}

/**
 * Q&A data from the backend.
 */
interface NewspaperQA {
    id: number;
    question: string;
    answer: string | null;
}

/**
 * Comic data from the backend.
 */
interface NewspaperComic {
    id: number;
    title: string | null;
    slug: string;
    artist_name: string;
    artist_url: string | null;
    thumbnail_url: string | null;
}

/**
 * Props for the Index page.
 */
interface Props {
    issue: NewspaperIssue | null;
    articles: NewspaperArticle[];
    latestQA: NewspaperQA[];
    latestComic: NewspaperComic | null;
    hasArchives: boolean;
    isGm: boolean;
}

/**
 * Public Newspaper Index page - the front page of The Feywild Times.
 * Displays the latest issue with all its articles in a traditional newspaper-style layout.
 */
export default function Index({ issue, articles, latestQA, latestComic, hasArchives, isGm }: Props) {
    return (
        <>
            <Head title="The Feywild Times" />

            <div className="min-h-screen bg-gray-50">
                {/* Newspaper Header */}
                <header className="bg-white border-b-4 border-double border-gray-900 py-6">
                    <div className="max-w-6xl mx-auto px-4 text-center">
                        {/* Issue info */}
                        {issue && (
                            <p className="text-sm text-gray-600 font-serif mb-1">
                                {issue.display_title}
                                {!issue.is_published && isGm && (
                                    <span className="ml-2 px-2 py-0.5 bg-yellow-500 text-white rounded text-xs">
                                        Draft
                                    </span>
                                )}
                            </p>
                        )}
                        <h1 className="text-5xl md:text-6xl font-serif font-bold text-black tracking-tight">
                            The Feywild Times
                        </h1>
                        <p className="text-gray-600 font-serif italic mt-2">
                            "100% Accurate Opinionated Reporting"
                        </p>
                        <div className="flex justify-center gap-4 mt-4 text-sm text-gray-700">
                            <Link
                                href="/newspaper/ask"
                                className="hover:text-gray-500 transition-colors underline"
                            >
                                Ask the Feywild Times
                            </Link>
                            <span className="text-gray-400">|</span>
                            <Link
                                href="/newspaper/comics"
                                className="hover:text-gray-500 transition-colors underline"
                            >
                                Features
                            </Link>
                            {hasArchives && (
                                <>
                                    <span className="text-gray-400">|</span>
                                    <Link
                                        href="/newspaper/archives"
                                        className="hover:text-gray-500 transition-colors underline"
                                    >
                                        Archives
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </header>

                {/* Main Content */}
                <main className="max-w-6xl mx-auto px-4 py-6">
                    {!issue || articles.length === 0 ? (
                        <EmptyNewspaper />
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                            {/* Main Articles Column */}
                            <div className="lg:col-span-2 space-y-4">
                                {/* Featured Article (first one) */}
                                {articles[0] && (
                                    <FeaturedArticle article={articles[0]} isGm={isGm} />
                                )}

                                {/* Other Articles */}
                                {articles.length > 1 && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {articles.slice(1).map((article) => (
                                            <ArticleCard key={article.id} article={article} isGm={isGm} />
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Sidebar */}
                            <aside className="space-y-4">
                                {/* Ask the Feywild Times */}
                                {latestQA.length > 0 && (
                                    <div className="bg-white border border-gray-300 rounded-lg p-3">
                                        <h2 className="text-lg font-serif font-bold text-black mb-3 border-b border-gray-300 pb-2">
                                            Ask the Feywild Times
                                        </h2>
                                        <div className="space-y-3">
                                            {latestQA.map((qa) => (
                                                <div key={qa.id} className="text-sm">
                                                    <p className="font-medium text-gray-900 italic">
                                                        "{qa.question}"
                                                    </p>
                                                    <p className="text-gray-700 mt-1 line-clamp-3">
                                                        {qa.answer}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                        <Link
                                            href="/newspaper/ask"
                                            className="block mt-3 text-center text-sm text-gray-700 hover:text-gray-500 underline"
                                        >
                                            Read more Q&A
                                        </Link>
                                    </div>
                                )}

                                {/* Latest Comic */}
                                {latestComic && (
                                    <div className="bg-white border border-gray-300 rounded-lg p-3">
                                        <h2 className="text-lg font-serif font-bold text-black mb-3 border-b border-gray-300 pb-2">
                                            Latest Feature
                                        </h2>
                                        <Link
                                            href={`/newspaper/comics/${latestComic.slug}`}
                                            className="block hover:opacity-90 transition-opacity"
                                        >
                                            {latestComic.thumbnail_url && (
                                                <img
                                                    src={latestComic.thumbnail_url}
                                                    alt={latestComic.title || `Feature by ${latestComic.artist_name}`}
                                                    className="w-full aspect-square object-cover rounded mb-2"
                                                />
                                            )}
                                            {latestComic.title && (
                                                <p className="font-medium text-gray-900 text-sm">
                                                    {latestComic.title}
                                                </p>
                                            )}
                                            <p className="text-xs text-gray-600">
                                                by {latestComic.artist_url ? (
                                                    <span className="underline">{latestComic.artist_name}</span>
                                                ) : (
                                                    latestComic.artist_name
                                                )}
                                            </p>
                                        </Link>
                                        <Link
                                            href="/newspaper/comics"
                                            className="block mt-3 text-center text-sm text-gray-700 hover:text-gray-500 underline"
                                        >
                                            View all Features
                                        </Link>
                                    </div>
                                )}

                                {/* Archives Link in sidebar */}
                                {hasArchives && (
                                    <div className="bg-white border border-gray-300 rounded-lg p-3">
                                        <h2 className="text-lg font-serif font-bold text-black mb-3 border-b border-gray-300 pb-2">
                                            Archives
                                        </h2>
                                        <p className="text-sm text-gray-700 mb-3">
                                            Read past issues of The Feywild Times
                                        </p>
                                        <Link
                                            href="/newspaper/archives"
                                            className="block text-center text-sm text-gray-700 hover:text-gray-500 underline"
                                        >
                                            Browse Archives
                                        </Link>
                                    </div>
                                )}
                            </aside>
                        </div>
                    )}
                </main>
            </div>
        </>
    );
}

/**
 * Empty state when no issue/articles exist.
 */
function EmptyNewspaper() {
    return (
        <div className="text-center py-16">
            <div className="text-6xl mb-6">📰</div>
            <h2 className="text-2xl font-serif text-black mb-4">
                Extra! Extra! Coming Soon!
            </h2>
            <p className="text-gray-700 max-w-lg mx-auto">
                The presses are warming up. Check back soon for the latest news
                from across the Faewild realm.
            </p>
        </div>
    );
}

/**
 * Featured article - large display for the first/main article.
 */
function FeaturedArticle({ article, isGm }: { article: NewspaperArticle; isGm: boolean }) {
    return (
        <Link
            href={`/newspaper/${article.slug}`}
            className="block bg-white border border-gray-300 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
        >
            {article.cover_image_url && (
                <img
                    src={article.cover_image_url}
                    alt={article.title}
                    className="w-full h-64 object-cover"
                />
            )}
            <div className="p-4">
                <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                    {article.published_at && (
                        <span>{new Date(article.published_at).toLocaleDateString()}</span>
                    )}
                    {isGm && !article.is_published && (
                        <span className="px-2 py-0.5 bg-yellow-500 text-white rounded text-xs">Draft</span>
                    )}
                </div>
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-black mb-2 leading-tight">
                    {article.title}
                </h2>
                <p className="text-sm text-gray-600 mb-2">By {article.byline}</p>
                {article.excerpt && (
                    <p className="text-gray-700 line-clamp-3">{article.excerpt}</p>
                )}
            </div>
        </Link>
    );
}

/**
 * Article card - smaller display for secondary articles.
 */
function ArticleCard({ article, isGm }: { article: NewspaperArticle; isGm: boolean }) {
    return (
        <Link
            href={`/newspaper/${article.slug}`}
            className="block bg-white border border-gray-300 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
        >
            {article.cover_image_url && (
                <img
                    src={article.cover_image_url}
                    alt={article.title}
                    className="w-full h-32 object-cover"
                />
            )}
            <div className="p-3">
                <div className="flex items-center gap-2 text-xs text-gray-600 mb-1">
                    {article.published_at && (
                        <span>{new Date(article.published_at).toLocaleDateString()}</span>
                    )}
                    {isGm && !article.is_published && (
                        <span className="px-1.5 py-0.5 bg-yellow-500 text-white rounded text-xs">Draft</span>
                    )}
                </div>
                <h3 className="text-xl font-serif font-bold text-black mb-1 line-clamp-2 leading-tight">
                    {article.title}
                </h3>
                <p className="text-xs text-gray-600 mb-2">By {article.byline}</p>
                {article.excerpt && (
                    <p className="text-sm text-gray-700 line-clamp-2">{article.excerpt}</p>
                )}
            </div>
        </Link>
    );
}
